import { useState } from 'react';
import { useAdminData } from '../../admin/AdminDataContext';
import { featureFlags } from '../../lib/featureFlags';
import type { LayerConfig } from '../../map/demoLayers';
import { useMapContext, type MeasureMode, type MeasureShape } from '../../map/MapContext';
import {
  BasemapIcon,
  BoxSelectIcon,
  ClearSelectionIcon,
  CoordinatesIcon,
  DeleteSelectionIcon,
  FullscreenIcon,
  HomeExtentIcon,
  LegendIcon,
  LocateIcon,
  MeasureAreaIcon,
  MeasureDistanceIcon,
  PrintMapIcon,
  ShareIcon,
  SpatialQueryIcon,
  TerritoryCalculatorIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from '../icons/MapToolIcons';

function ControlButton({
  title,
  active,
  onClick,
  children,
}: {
  title: string;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`flex size-10 items-center justify-center rounded-[10px] border transition-colors ${
        active
          ? 'border-action-primary bg-action-primary text-text-inverse'
          : 'border-border-default bg-bg-surface text-text-primary hover:bg-bg-surface-secondary'
      }`}
    >
      {children}
    </button>
  );
}

/** Every popup's header — a title plus a close (×) button, so a panel never has
 * to be dismissed by re-clicking its trigger icon or clicking elsewhere. */
function PanelHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <p className="text-xs font-semibold text-text-primary">{title}</p>
      <button
        type="button"
        onClick={onClose}
        title="Закрыть"
        className="flex size-6 shrink-0 items-center justify-center rounded-md text-text-secondary hover:bg-bg-surface-secondary"
      >
        ✕
      </button>
    </div>
  );
}

/** One row in the legend popover — a color dot, the layer name, an optional
 * object count (LegendConfig.showObjectCounts), and an optional inline
 * opacity slider (LegendConfig.showOpacitySlider) so adjusting a layer's
 * opacity doesn't require opening the separate "Слои и фильтры" panel. */
function LegendEntry({
  layer,
  visible,
  opacity,
  showCount,
  showOpacitySlider,
  onOpacityChange,
}: {
  layer: LayerConfig;
  visible: boolean;
  opacity: number;
  showCount: boolean;
  showOpacitySlider: boolean;
  onOpacityChange: (value: number) => void;
}) {
  return (
    <div className={visible ? '' : 'opacity-40'}>
      <div className="flex items-center gap-2">
        <span className="size-3 shrink-0 rounded-full" style={{ backgroundColor: layer.color }} />
        <span className="flex-1 truncate text-[11px] text-text-primary">{layer.name}</span>
        {showCount && <span className="shrink-0 text-[10px] text-text-secondary">{layer.count.toLocaleString('ru-RU')}</span>}
      </div>
      {showOpacitySlider && (
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={opacity}
          onChange={(e) => onOpacityChange(Number(e.target.value))}
          className="mt-1 w-full accent-action-primary"
        />
      )}
    </div>
  );
}

const measureModeLabels: { mode: MeasureMode; label: string }[] = [
  { mode: 'distance', label: 'Расстояние' },
  { mode: 'area', label: 'Площадь' },
  { mode: 'perimeter', label: 'Периметр' },
];

const shapeLabels: { shape: MeasureShape; label: string }[] = [
  { shape: 'polygon', label: 'Полигон' },
  { shape: 'circle', label: 'Круг' },
  { shape: 'rectangle', label: 'Прямоугольник' },
];

/** Every toggleable popup on this toolbar — kept as one union so opening any one
 * of them always closes whichever other one was open (no more overlapping
 * panels stacking on top of each other). */
type PanelKey = 'coordinates' | 'measure' | 'basemap' | 'legend' | 'share';

export function MapControls() {
  const {
    zoomIn,
    zoomOut,
    locate,
    locating,
    locateError,
    clearLocateError,
    activeBasemapId,
    setBasemap,
    layers,
    layerVisibility,
    layerOpacity,
    setLayerOpacity,
    measureActive,
    measureMode,
    startMeasure,
    stopMeasure,
    boxSelectActive,
    startBoxSelect,
    stopBoxSelect,
    spatialQueryActive,
    startSpatialQuery,
    stopSpatialQuery,
    objectCalcActive,
    startObjectCalc,
    stopObjectCalc,
    drawActive,
    drawLayerId,
    selectedFeature,
    selectionResults,
    deleteSelectedFeature,
    deleteSelectionResults,
    fitToLayers,
    flyToCoordinates,
    getViewState,
  } = useMapContext();
  const { mapSettings, basemaps, layerGroups } = useAdminData();
  const enabledBasemaps = basemaps.filter((b) => b.enabled);
  const tools = mapSettings?.tools;
  const legendConfig = mapSettings?.legend;
  const [openPanel, setOpenPanel] = useState<PanelKey | null>(null);
  const togglePanel = (key: PanelKey) => setOpenPanel((prev) => (prev === key ? null : key));
  const [shared, setShared] = useState(false);
  const [maxCopied, setMaxCopied] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [coordLat, setCoordLat] = useState('');
  const [coordLon, setCoordLon] = useState('');
  const [coordError, setCoordError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const shareUrl = () => {
    const state = getViewState();
    const url = new URL(window.location.href);
    if (state) {
      url.searchParams.set('lat', state.lat.toFixed(6));
      url.searchParams.set('lon', state.lon.toFixed(6));
      url.searchParams.set('z', String(Math.round(state.zoom * 100) / 100));
    }
    return url.toString();
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl());
      setShared(true);
      setTimeout(() => setShared(false), 1500);
    } catch {
      // буфер обмена недоступен (например, без HTTPS) — тихо игнорируем
    }
  };

  const shareText = () => `Карта «${mapSettings?.title ?? 'КСЕНУС ГИС'}»`;

  const handleTelegramShare = () => {
    const url = new URL('https://t.me/share/url');
    url.searchParams.set('url', shareUrl());
    url.searchParams.set('text', shareText());
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  };

  const handleVkShare = () => {
    const url = new URL('https://vk.com/share.php');
    url.searchParams.set('url', shareUrl());
    url.searchParams.set('title', shareText());
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  };

  const handleMaxShare = async () => {
    // MAX, unlike Telegram/VK, doesn't publish a "share this URL" web intent —
    // the OS/browser native share sheet (where MAX shows up as a target if the
    // app is installed) is the only real integration point today. Falls back
    // to copying the link wherever the Web Share API isn't available (most
    // desktop browsers).
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: shareText(), url: shareUrl() });
        return;
      } catch {
        // отменено пользователем или API недоступно для этого источника — копируем ссылку
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl());
      setMaxCopied(true);
      setTimeout(() => setMaxCopied(false), 1500);
    } catch {
      // буфер обмена недоступен — тихо игнорируем
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setFullscreen(false)).catch(() => {});
    }
  };

  const handlePrint = () => window.print();

  const handleSubmitCoordinates = () => {
    const lat = Number(coordLat.replace(',', '.'));
    const lon = Number(coordLon.replace(',', '.'));
    if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
      setCoordError('Широта должна быть числом от -90 до 90');
      return;
    }
    if (!Number.isFinite(lon) || lon < -180 || lon > 180) {
      setCoordError('Долгота должна быть числом от -180 до 180');
      return;
    }
    setCoordError(null);
    flyToCoordinates(lon, lat);
    setOpenPanel(null);
  };

  const hasEditableSelection = drawActive && Boolean(drawLayerId);
  const selectionCountInEditLayer = selectionResults.filter((r) => r.layerId === drawLayerId).length;

  const handleDeleteSelected = async () => {
    setDeleting(true);
    try {
      if (selectionResults.length > 0) await deleteSelectionResults();
      else if (selectedFeature) await deleteSelectedFeature();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="absolute right-6 top-[84px] z-10 flex flex-col gap-2">
      <ControlButton title="Приблизить" onClick={zoomIn}>
        <ZoomInIcon className="size-5" />
      </ControlButton>
      <ControlButton title="Отдалить" onClick={zoomOut}>
        <ZoomOutIcon className="size-5" />
      </ControlButton>
      <ControlButton title="Исходный масштаб" onClick={fitToLayers}>
        <HomeExtentIcon className="size-5" />
      </ControlButton>

      <div className="my-1 h-px bg-border-default" />

      {tools?.locate !== false && (
        <div className="relative">
          <ControlButton title={locateError ?? 'Моё местоположение'} onClick={locate} active={locating}>
            <LocateIcon className={`size-5 ${locating ? 'animate-pulse' : ''}`} />
          </ControlButton>
          {locateError && (
            <div className="absolute right-12 top-0 w-[min(320px,calc(100vw-88px))] rounded-lg border border-status-danger bg-bg-surface p-2.5 shadow-lg">
              <PanelHeader title="Геолокация" onClose={clearLocateError} />
              <p className="mt-1 text-[11px] font-medium leading-snug text-status-danger">{locateError}</p>
            </div>
          )}
        </div>
      )}

      {tools?.coordinates !== false && (
        <div className="relative">
          <ControlButton
            title="Ввод координат"
            onClick={() => {
              togglePanel('coordinates');
              setCoordError(null);
            }}
            active={openPanel === 'coordinates'}
          >
            <CoordinatesIcon className="size-5" />
          </ControlButton>
          {openPanel === 'coordinates' && (
            <div className="absolute right-12 top-0 flex w-[min(256px,calc(100vw-88px))] flex-col gap-2 rounded-xl border border-border-default bg-bg-surface p-3 shadow-lg">
              <PanelHeader title="Перейти по координатам" onClose={() => setOpenPanel(null)} />
              <label className="text-[10px] font-medium text-text-secondary">Широта</label>
              <input
                value={coordLat}
                onChange={(e) => setCoordLat(e.target.value)}
                placeholder="55.75580"
                className="h-9 rounded-lg border border-border-default bg-bg-surface px-2.5 text-xs text-text-primary outline-none focus:border-action-primary"
              />
              <label className="text-[10px] font-medium text-text-secondary">Долгота</label>
              <input
                value={coordLon}
                onChange={(e) => setCoordLon(e.target.value)}
                placeholder="37.61730"
                className="h-9 rounded-lg border border-border-default bg-bg-surface px-2.5 text-xs text-text-primary outline-none focus:border-action-primary"
              />
              {coordError && <p className="text-[10px] font-medium text-status-danger">{coordError}</p>}
              <button
                type="button"
                onClick={handleSubmitCoordinates}
                className="mt-1 flex h-9 items-center justify-center rounded-lg bg-action-primary text-xs font-medium text-text-inverse hover:bg-action-primary-hover"
              >
                Перейти
              </button>
            </div>
          )}
        </div>
      )}

      <div className="my-1 h-px bg-border-default" />

      {tools?.measure !== false && (
        <div className="flex items-center gap-1.5">
          <div className="relative">
            <ControlButton
              title="Измерение"
              onClick={() => (measureActive ? stopMeasure() : togglePanel('measure'))}
              active={measureActive || openPanel === 'measure'}
            >
              <MeasureDistanceIcon className="size-5" />
            </ControlButton>
            {openPanel === 'measure' && !measureActive && (
              <div className="absolute right-12 top-0 flex w-[min(288px,calc(100vw-88px))] flex-col gap-3 rounded-xl border border-border-default bg-bg-surface p-3 shadow-lg">
                <PanelHeader title="Измерение" onClose={() => setOpenPanel(null)} />
                {measureModeLabels.map(({ mode, label }) => (
                  <div key={mode}>
                    <p className="mb-1.5 text-[11px] font-semibold text-text-primary">{label}</p>
                    {mode === 'distance' ? (
                      <button
                        type="button"
                        onClick={() => {
                          startMeasure('distance');
                          setOpenPanel(null);
                        }}
                        className="flex h-9 w-full items-center gap-2 rounded-lg px-2.5 text-left text-xs font-medium text-text-primary hover:bg-bg-surface-secondary"
                      >
                        <MeasureDistanceIcon className="size-4 shrink-0" />
                        Начать измерение
                      </button>
                    ) : (
                      <div className="flex flex-col gap-1">
                        {shapeLabels.map(({ shape, label: shapeLabel }) => (
                          <button
                            key={shape}
                            type="button"
                            onClick={() => {
                              startMeasure(mode, shape);
                              setOpenPanel(null);
                            }}
                            className="flex h-9 w-full items-center gap-2 rounded-lg px-2.5 text-left text-xs font-medium text-text-primary hover:bg-bg-surface-secondary"
                          >
                            <MeasureAreaIcon className="size-4 shrink-0" />
                            {shapeLabel}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          {measureMode !== null && (
            <ControlButton title="Сбросить измерение" onClick={stopMeasure}>
              <ClearSelectionIcon className="size-5" />
            </ControlButton>
          )}
        </div>
      )}

      {tools?.boxSelect !== false && (
        <ControlButton
          title="Выбор рамкой (все видимые слои)"
          onClick={() => (boxSelectActive ? stopBoxSelect() : startBoxSelect('all'))}
          active={boxSelectActive}
        >
          <BoxSelectIcon className="size-5" />
        </ControlButton>
      )}

      {tools?.spatialQuery !== false && (
        <ControlButton
          title="Пространственные запросы"
          onClick={() => (spatialQueryActive ? stopSpatialQuery() : startSpatialQuery())}
          active={spatialQueryActive}
        >
          <SpatialQueryIcon className="size-5" />
        </ControlButton>
      )}

      {/* Прототип под тендер по наружному освещению — см. src/lib/featureFlags.ts */}
      {featureFlags.objectCalculatorOnMap && tools?.objectCalculator !== false && (
        <ControlButton
          title="Объектный калькулятор — кликайте объекты одного типа, чтобы посчитать сумму"
          onClick={() => (objectCalcActive ? stopObjectCalc() : startObjectCalc())}
          active={objectCalcActive}
        >
          <TerritoryCalculatorIcon className="size-5" />
        </ControlButton>
      )}

      {hasEditableSelection && (selectedFeature || selectionResults.length > 0) && (
        <ControlButton
          title={
            selectionResults.length > 0
              ? `Удалить выбранные в редактируемом слое (${selectionCountInEditLayer})`
              : 'Удалить объект'
          }
          onClick={handleDeleteSelected}
          active={deleting}
        >
          <DeleteSelectionIcon className="size-5" />
        </ControlButton>
      )}

      <div className="my-1 h-px bg-border-default" />

      <div className="relative">
        <ControlButton title="Подложка" onClick={() => togglePanel('basemap')} active={openPanel === 'basemap'}>
          <BasemapIcon className="size-5" />
        </ControlButton>
        {openPanel === 'basemap' && (
          <div className="absolute right-12 top-0 flex w-[min(192px,calc(100vw-88px))] flex-col gap-1 rounded-xl border border-border-default bg-bg-surface p-2 shadow-lg">
            <div className="px-1 pb-1">
              <PanelHeader title="Подложка" onClose={() => setOpenPanel(null)} />
            </div>
            {enabledBasemaps.length === 0 ? (
              <p className="px-3 py-2 text-[11px] text-text-secondary">Нет доступных подложек</p>
            ) : (
              enabledBasemaps.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => {
                    setBasemap(b.id);
                    setOpenPanel(null);
                  }}
                  className={`truncate rounded-lg px-3 py-2 text-left text-xs font-medium ${
                    activeBasemapId === b.id ? 'bg-action-primary text-text-inverse' : 'text-text-primary hover:bg-bg-surface-secondary'
                  }`}
                >
                  {b.name}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {tools?.legend !== false && (
        <div className="relative">
          <ControlButton title="Легенда" onClick={() => togglePanel('legend')} active={openPanel === 'legend'}>
            <LegendIcon className="size-5" />
          </ControlButton>
          {openPanel === 'legend' && (
            <div className="absolute right-12 top-0 flex max-h-[420px] w-[min(256px,calc(100vw-88px))] flex-col gap-2 overflow-y-auto rounded-xl border border-border-default bg-bg-surface p-3 shadow-lg">
              <PanelHeader title="Легенда" onClose={() => setOpenPanel(null)} />
              {legendConfig?.groupByLayerGroup
                ? Object.entries(
                    layers.reduce<Record<string, LayerConfig[]>>((acc, layer) => {
                      const groupName = layerGroups.find((g) => g.name === layer.group)?.name ?? 'Без группы';
                      (acc[groupName] ??= []).push(layer);
                      return acc;
                    }, {}),
                  ).map(([groupName, groupLayers]) => (
                    <div key={groupName} className="flex flex-col gap-1.5">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-text-secondary">{groupName}</p>
                      {groupLayers.map((layer) => (
                        <LegendEntry
                          key={layer.id}
                          layer={layer}
                          visible={layerVisibility[layer.id]}
                          opacity={layerOpacity[layer.id] ?? 1}
                          showCount={legendConfig?.showObjectCounts ?? false}
                          showOpacitySlider={legendConfig?.showOpacitySlider ?? false}
                          onOpacityChange={(v) => setLayerOpacity(layer.id, v)}
                        />
                      ))}
                    </div>
                  ))
                : layers.map((layer) => (
                    <LegendEntry
                      key={layer.id}
                      layer={layer}
                      visible={layerVisibility[layer.id]}
                      opacity={layerOpacity[layer.id] ?? 1}
                      showCount={legendConfig?.showObjectCounts ?? false}
                      showOpacitySlider={legendConfig?.showOpacitySlider ?? false}
                      onOpacityChange={(v) => setLayerOpacity(layer.id, v)}
                    />
                  ))}
            </div>
          )}
        </div>
      )}

      {tools?.share !== false && (
        <div className="relative">
          <ControlButton title="Поделиться" onClick={() => togglePanel('share')} active={openPanel === 'share'}>
            <ShareIcon className="size-5" />
          </ControlButton>
          {openPanel === 'share' && (
            <div className="absolute right-12 top-0 flex w-[min(256px,calc(100vw-88px))] flex-col gap-3 rounded-xl border border-border-default bg-bg-surface p-3 shadow-lg">
              <PanelHeader title="Поделиться картой" onClose={() => setOpenPanel(null)} />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleTelegramShare}
                  className="flex h-9 flex-1 items-center justify-center rounded-lg border border-border-default text-[11px] font-medium text-text-secondary hover:bg-bg-surface-secondary"
                >
                  Telegram
                </button>
                <button
                  type="button"
                  onClick={handleVkShare}
                  className="flex h-9 flex-1 items-center justify-center rounded-lg border border-border-default text-[11px] font-medium text-text-secondary hover:bg-bg-surface-secondary"
                >
                  ВКонтакте
                </button>
                <button
                  type="button"
                  onClick={handleMaxShare}
                  title="MAX не публикует веб-ссылку «поделиться» — откроется системное меню «Поделиться» или ссылка скопируется"
                  className="flex h-9 flex-1 items-center justify-center rounded-lg border border-border-default text-[11px] font-medium text-text-secondary hover:bg-bg-surface-secondary"
                >
                  {maxCopied ? 'Скопировано ✓' : 'MAX'}
                </button>
              </div>
              <button
                type="button"
                onClick={handleCopyLink}
                className="flex h-9 items-center justify-center rounded-lg bg-action-primary text-xs font-medium text-text-inverse hover:bg-action-primary-hover"
              >
                {shared ? 'Ссылка скопирована ✓' : 'Скопировать ссылку'}
              </button>
              <p className="text-[10px] text-text-secondary">Ссылка сохраняет текущее положение карты — по ней сразу откроется этот вид.</p>
            </div>
          )}
        </div>
      )}

      {tools?.printMap !== false && (
        <ControlButton title="Печать карты" onClick={handlePrint}>
          <PrintMapIcon className="size-5" />
        </ControlButton>
      )}

      <ControlButton title={fullscreen ? 'Выйти из полноэкранного режима' : 'На весь экран'} onClick={toggleFullscreen} active={fullscreen}>
        <FullscreenIcon className="size-5" />
      </ControlButton>
    </div>
  );
}
